import { useEffect } from 'react'
import { useTheme, type Theme } from '@/stores/local-settings.store'

// The page background for each theme (--background in styles.css), so the
// mobile status bar and browser chrome blend into the top of the page.
export const THEME_COLORS = { light: '#f7f5f1', dark: '#0a0a0b' } as const

function applyTheme(theme: Theme, systemPrefersDark: boolean) {
  const isDark = theme === 'dark' || (theme === 'system' && systemPrefersDark)

  if (isDark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }

  // index.html ships one tag per OS scheme. An explicit in-app theme can
  // disagree with the OS, so point every tag at the theme actually shown.
  const color = isDark ? THEME_COLORS.dark : THEME_COLORS.light
  document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]').forEach((meta) => {
    meta.content = color
  })
}

export function useThemeEffect() {
  const theme = useTheme()

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const root = document.documentElement
    let firstFrame = 0
    let secondFrame = 0

    const applyThemeWithoutTransitions = (systemPrefersDark: boolean) => {
      cancelAnimationFrame(firstFrame)
      cancelAnimationFrame(secondFrame)
      root.classList.add('theme-switching')
      applyTheme(theme, systemPrefersDark)

      firstFrame = requestAnimationFrame(() => {
        secondFrame = requestAnimationFrame(() => {
          root.classList.remove('theme-switching')
        })
      })
    }

    applyThemeWithoutTransitions(mediaQuery.matches)

    // Listen for system preference changes when using 'system' mode
    const handleChange = (e: MediaQueryListEvent) => {
      if (theme === 'system') {
        applyThemeWithoutTransitions(e.matches)
      }
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => {
      mediaQuery.removeEventListener('change', handleChange)
      cancelAnimationFrame(firstFrame)
      cancelAnimationFrame(secondFrame)
      root.classList.remove('theme-switching')
    }
  }, [theme])
}
